import { ASSET_MANAGER } from "../main.js";
import { PlayerController } from "../player/playerController.js";
import { ShelfController, SHELF_SCALE } from "../shelves/shelfController.js";
import { DoorTrigger } from "./doorTrigger.js";
import { BoundingBox } from "../../componentLibrary/boundingBox.js";
import { MovementComponent } from "../../componentLibrary/movementComponent.js";
import { ItemEntity } from "../ordermanagement/itemEntity.js";
import { DeliveryController } from "../deliveryEntity/deliveryController.js";
import { MonsterEntity } from "../monster/monsterEntity.js";
import { StoreFloor } from "./storeInterior/storeFloorController.js";
import { BloodController } from "./storeInterior/bloodSplatterController.js";
import { ShelfShadow } from "./storeInterior/shelfShadowController.js";
import { UpdatePoint } from "../monster/updatePointEntity.js";
import { WallEntity } from "./wallEntity.js";
import { staticPositionComponent } from "../../componentLibrary/staticPositionComponent.js";
import { ParkingLot } from "./storeExterior/parkingLotController.js";
import { Ball } from "./storeExterior/ballController.js";
import { Bush } from "./storeExterior/bushController.js";
import { VehicleEntity } from "./storeExterior/vehicleEntity.js";
import { FloorGrid } from "./storeInterior/floorGrid.js";
import { PlayerLight } from "../player/playerLight.js";
import { SelfCheckout } from "./storeInterior/selfCheckoutController.js";
import { ShoppingCart } from "./storeInterior/shoppingCartController.js";
import { WallSpriteController } from "./storeInterior/wallSpriteController.js";
import { FlickerLightController } from "./storeInterior/flickerLightController.js";
import { Vignette } from "./storeInterior/vignetteController.js";
/** Coordinate on actual shelves describing where items can be placed before scaling  */
const ITEM_HSHELF_POSITION = [
    { x: 30, y: 20 },
    { x: 8, y: 20 },
    { x: 52, y: 20 }
];
// TODO: i'd like to make it so items exist in diff locations depending on the number of 
// rows the shelf has, since some shelves have only 2 rows and then items spawn looking like 
// they're floating rather than sitting on the shelf
/**
 * Altered base class for all room scenes.
 * Handles all common setup logic so individual rooms only
 * ned to define their data via abstract methods.
 *
 * @author Luke Willis, Claude Sonnet 4.5, Preston Sia
 */
export class BaseRoomScene {
    constructor(game, roomData, allowedRoomIds, orderLoop) {
        this.roomData = roomData;
        this.inputSystem = game.getInputSystem();
        this.collisionSystem = game.getCollisionSystem();
        this.localEntities = [];
        this.orderLoop = orderLoop;
        this.allowedRoomIds = allowedRoomIds;
    }
    /**
     * Loads scene data and adds them as actual instances
     * to the scene manager.
     * @param sceneManager Scene manager
     */
    onEnter(sceneManager) {
        var _a, _b, _c, _d, _e, _f, _g;
        console.log("Loading scene " + this.roomData.sceneId);
        // Attempt to find the current player
        let player;
        const existingPlayer = sceneManager.getLevelEntities().find(entity => entity instanceof PlayerController);
        if (existingPlayer) {
            player = existingPlayer;
            // Reuse existing player, just update spawn point
            player = existingPlayer;
            const movementComponent = player.getComponent(MovementComponent);
            if (movementComponent) {
                this.movePlayer(movementComponent);
                /* Create and load monsters */
                for (const monsterSpawn of this.roomData.monsterSpawns) {
                    const monster = new MonsterEntity(monsterSpawn, 5, movementComponent, this.roomData.updatePoints.slice());
                    sceneManager.addEntity(monster);
                    this.localEntities.push(monster);
                    this.collisionSystem.addEntity(monster);
                }
                /* Create player light */
                const playerLight = new PlayerLight(movementComponent);
                sceneManager.addEntity(playerLight);
                this.localEntities.push(playerLight);
            }
        }
        else {
            player = null;
            console.error("Player not found during scene load");
        }
        /* Create update points */
        for (const updatePoint of this.roomData.updatePoints) {
            const pointTrigger = new UpdatePoint(updatePoint);
            this.localEntities.push(pointTrigger);
            sceneManager.addEntity(pointTrigger);
            this.collisionSystem.addEntity(pointTrigger);
        }
        /* Create walls */
        const topWall = new WallEntity(new staticPositionComponent({ x: 0, y: 0 }), 1280, 5);
        const bottomWall = new WallEntity(new staticPositionComponent({ x: 0, y: 705 }), 1280, 5);
        const leftWall = new WallEntity(new staticPositionComponent({ x: 0, y: 0 }), 5, 720);
        const rightWall = new WallEntity(new staticPositionComponent({ x: 1265, y: 0 }), 5, 720);
        sceneManager.addEntity(topWall);
        sceneManager.addEntity(bottomWall);
        sceneManager.addEntity(leftWall);
        sceneManager.addEntity(rightWall);
        this.localEntities.push(topWall);
        this.localEntities.push(bottomWall);
        this.localEntities.push(leftWall);
        this.localEntities.push(rightWall);
        this.collisionSystem.addEntity(topWall);
        this.collisionSystem.addEntity(bottomWall);
        this.collisionSystem.addEntity(rightWall);
        this.collisionSystem.addEntity(leftWall);
        /* Create vignette */
        const vignette = new Vignette();
        this.localEntities.push(vignette);
        sceneManager.addEntity(vignette);
        /* Create and load shelving and add items */
        const allowedItems = this.roomData.allowedItems.slice(); // using slice to get a shallow copy
        for (const shelfData of this.roomData.shelves) {
            const shelfSprite = ASSET_MANAGER.getImageAsset(shelfData.spriteId);
            if (shelfSprite === null) {
                throw new Error(`Failed to load shelf sprite: "${shelfData.spriteId}"`);
            }
            const shelf = new ShelfController(shelfData.position, shelfSprite, shelfData.shelfNum);
            /* Shelf shadow */
            const shelfShadow = new ShelfShadow(shelfData.position);
            this.localEntities.push(shelfShadow);
            sceneManager.addEntity(shelfShadow);
            // if the array has enough items to fill the shelf, retrive as many as will fit up to the max. Otherwise, retrieve whatever's available.
            const numItems = allowedItems.length >= ITEM_HSHELF_POSITION.length ? ITEM_HSHELF_POSITION.length : allowedItems.length;
            const shelfItems = allowedItems.splice(0, numItems);
            for (let i = 0; i < shelfItems.length; i++) {
                const shelfItem = shelfItems[i];
                if (shelfItem) {
                    const itemPos = {
                        x: (_b = (_a = ITEM_HSHELF_POSITION[i]) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0,
                        y: (_d = (_c = ITEM_HSHELF_POSITION[i]) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0
                    };
                    itemPos.x = (itemPos.x * SHELF_SCALE) + shelfData.position.x;
                    itemPos.y = (itemPos.y * SHELF_SCALE) + shelfData.position.y;
                    const itemEntity = new ItemEntity(shelfItem, itemPos);
                    // Register the item as an observer of the order loop
                    (_e = this.orderLoop) === null || _e === void 0 ? void 0 : _e.subscribe(itemEntity);
                    // [hack] also determine if the item should be flashing upon instantiation; this method can be loaded after an order is already made active
                    if (this.orderLoop.getCurrentActiveOrder()) {
                        if ((_f = this.orderLoop.getCurrentActiveOrder()) === null || _f === void 0 ? void 0 : _f.hasItem(itemEntity.getItemType())) {
                            itemEntity.enablePulsing();
                        }
                    }
                    sceneManager.addEntity(itemEntity);
                    this.collisionSystem.addEntity(itemEntity);
                    this.localEntities.push(itemEntity);
                }
            }
            this.localEntities.push(shelf);
            sceneManager.addEntity(shelf);
            this.collisionSystem.addEntity(shelf);
        }
        /* Create door triggers */
        if (player) {
            const playerBoundingBox = player.getComponent(BoundingBox);
            if (!playerBoundingBox) {
                throw new Error("Player is missing a BoundingBox component");
            }
            for (const door of this.roomData.doors) {
                if (this.allowedRoomIds.includes(door.targetSceneId)) {
                    const trigger = new DoorTrigger(door.position, door.size, door.targetSceneId, door.direction, sceneManager, playerBoundingBox);
                    this.localEntities.push(trigger);
                    sceneManager.addEntity(trigger);
                    this.collisionSystem.addEntity(trigger);
                }
            }
        }
        for (const wallSprite of this.roomData.wallSprites) {
            const wallSpriteEntity = new WallSpriteController(wallSprite.direction, wallSprite.position, wallSprite.size, wallSprite.cornerType, wallSprite.cornerPos);
            sceneManager.addEntity(wallSpriteEntity);
            this.localEntities.push(wallSpriteEntity);
            this.collisionSystem.addEntity(wallSpriteEntity);
        }
        if (this.roomData.isCheckout) {
            for (let i = 0; i < 3; i++) {
                // render checkout room sprites from bottom to top to layer properly
                // NOTE: i know this is prob not the best way to do this but it's easier 
                // than making a whole nother class to deal w this small logic
                let regSX = 75; /** render bottom checkout + cart */
                let regYPos = 450;
                let cartPos = { x: 1100, y: 105 };
                let cartSX = 41;
                let cartSW = 21;
                let cartSH = 19;
                if (i == 1) { /** render middle checkout + cart */
                    regSX = 38;
                    regYPos = 250;
                    cartPos = { x: 410, y: 590 };
                    cartSX = 26;
                    cartSW = 13;
                    cartSH = 21;
                }
                else if (i == 2) { /** render top checkout + cart */
                    regSX = 1;
                    regYPos = 50;
                    cartPos = { x: 330, y: 140 };
                    cartSX = 1;
                    cartSW = 23;
                    cartSH = 15;
                }
                const checkout = new SelfCheckout({ x: 50, y: regYPos }, regSX);
                const cart = new ShoppingCart(cartPos, cartSX, cartSW, cartSH);
                this.localEntities.push(checkout);
                sceneManager.addEntity(checkout);
                this.collisionSystem.addEntity(checkout);
                this.localEntities.push(cart);
                sceneManager.addEntity(cart);
                this.collisionSystem.addEntity(cart);
            }
        }
        /* Delivery Entity and Vehicle Entity */
        const deliveryPOS = this.roomData.deliveryEntityPosition;
        if (deliveryPOS) {
            const deliveryEntity = new DeliveryController(deliveryPOS, 1);
            // create one vehicle to be "recycled"
            const vehicle = new VehicleEntity({ x: -300, y: 200 }, 8);
            // draw vehicle before entity to display in proper order
            sceneManager.addEntity(vehicle);
            this.collisionSystem.addEntity(vehicle);
            this.localEntities.push(vehicle);
            (_g = this.orderLoop) === null || _g === void 0 ? void 0 : _g.subscribe(vehicle);
            this.localEntities.push(deliveryEntity);
            sceneManager.addEntity(deliveryEntity);
            this.collisionSystem.addEntity(deliveryEntity);
        }
        /* Blood splatters */
        for (const bloodPos of this.roomData.bloodLocations) {
            const blood = new BloodController(bloodPos);
            sceneManager.addEntity(blood);
            this.collisionSystem.addEntity(blood);
            this.localEntities.push(blood);
        }
        /* Floor texture */
        if (this.roomData.isParkingLot) {
            // const vehicle = new VehicleEntity(
            //   { x: -200, y: 200 },
            //   8
            // )
            // sceneManager.addEntity(vehicle);
            // this.collisionSystem.addEntity(vehicle);
            // this.localEntities.push(vehicle);
            /** Create bush for collision handling */
            const bush = new Bush();
            sceneManager.addEntity(bush);
            this.localEntities.push(bush);
            this.collisionSystem.addEntity(bush);
            /** Create balls for collision handling */
            const ball1Pos = new staticPositionComponent({ x: 32, y: 37 });
            const ball2Pos = new staticPositionComponent({ x: 352, y: 37 });
            const ball3Pos = new staticPositionComponent({ x: 848, y: 37 });
            const ball4Pos = new staticPositionComponent({ x: 1168, y: 37 });
            const ball1 = new Ball(ball1Pos);
            const ball2 = new Ball(ball2Pos);
            const ball3 = new Ball(ball3Pos);
            const ball4 = new Ball(ball4Pos);
            sceneManager.addEntity(ball1);
            sceneManager.addEntity(ball2);
            sceneManager.addEntity(ball3);
            sceneManager.addEntity(ball4);
            this.localEntities.push(ball1);
            this.localEntities.push(ball2);
            this.localEntities.push(ball3);
            this.localEntities.push(ball4);
            this.collisionSystem.addEntity(ball1);
            this.collisionSystem.addEntity(ball2);
            this.collisionSystem.addEntity(ball3);
            this.collisionSystem.addEntity(ball4);
            /** Create parking lot sprite */
            const lot = new ParkingLot();
            sceneManager.addEntity(lot);
            this.collisionSystem.addEntity(lot);
            this.localEntities.push(lot);
        }
        else {
            // One light per fluorescent tube position
            for (const lightConfig of this.roomData.lightConfig) {
                const light = new FlickerLightController(lightConfig);
                sceneManager.addEntity(light);
                this.localEntities.push(light);
            }
            const floorGrid = new FloorGrid();
            sceneManager.addEntity(floorGrid);
            this.collisionSystem.addEntity(floorGrid);
            this.localEntities.push(floorGrid);
            const floor = new StoreFloor();
            sceneManager.addEntity(floor);
            this.collisionSystem.addEntity(floor);
            this.localEntities.push(floor);
        }
    }
    /**
     * Called when the player returns to this room.
     * Re-registers cached entities with the SceneManager
     * so they get updated and drawn again — without
     * recreating them from scratch.
     */
    onResume(sceneManager) {
        console.log("Resuming " + this.roomData.sceneId);
        // Reposition player
        const existingPlayer = sceneManager.getLevelEntities().find(entity => entity instanceof PlayerController);
        if (existingPlayer) {
            const player = existingPlayer;
            const movementComponent = player.getComponent(MovementComponent);
            if (movementComponent) {
                this.movePlayer(movementComponent);
            }
        }
        // Re-add room entities
        for (const entity of this.localEntities) {
            //console.debug("Re-adding entity:", entity.constructor.name);
            sceneManager.addEntity(entity);
            this.collisionSystem.addEntity(entity);
        }
        //console.debug("onResume complete");
    }
    /**
     * Handles exiting the room by removing
     * entities from the core systems.
     */
    onExit() {
        //remove all room entities from collision system when leaving
        for (const entity of this.localEntities) {
            this.collisionSystem.removeEntity(entity);
        }
    }
    update(context) { }
    draw(context) { }
    movePlayer(movementComponent) {
        // if (this.roomData.defaultSpawn) { // if a default spawn is used, 
        //   movementComponent.setPosition({
        //     x: this.roomData.defaultSpawn.x,
        //     y: this.roomData.defaultSpawn.y
        //   });
        // }
        const currentPosition = {
            x: movementComponent.getPosition().x,
            y: movementComponent.getPosition().y
        };
        const spawnPts = this.roomData.spawnPoints.slice();
        // invert coordinates to calculate proper destination
        currentPosition.x = Math.abs(currentPosition.x - this.roomData.roomWidth);
        currentPosition.y = Math.abs(currentPosition.y - this.roomData.roomHeight);
        const nearestPoint = spawnPts.reduce((nearest, candidate) => {
            const distanceTo = (point) => Math.hypot(point.x - currentPosition.x, point.y - currentPosition.y);
            return distanceTo(candidate) < distanceTo(nearest) ? candidate : nearest;
        });
        movementComponent.setPosition({
            x: nearestPoint.x,
            y: nearestPoint.y
        });
    }
    /**
     * Helper method that takes in an entity and adds it to the scene manager,
     * collision system, and local entities
     *
     * @param sceneMgr the scene manager
     * @param e the entity to be added
     */
    loadEntities(sceneMgr, e) {
        sceneMgr.addEntity(e);
        this.localEntities.push(e);
        this.collisionSystem.addEntity(e);
    }
}
